
import { fetchServicesTranslations, SalesOrderInfo, selectServicesTranslations } from '../../../store/slices/salesOrders';
import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '../../../store/store';
import { IAccountInfo, selectAccountInfo, selectUserRole } from '../../../store/slices/auth';
import moment from 'moment';
import classes from './UpcomingList.module.css';
import { NURSE_USER_ROLE } from '../../../constants/userRolesConstants';
import { ConsultationTypes } from '../../../enums/enums';
import { IDoctor, selectDoctors } from '../../../store/slices/doctors';
import { useTranslation } from '../../../hooks/useTranslations';
import { buildTranslatedServicesMap, getTranslatedServicesName } from '../../../utils/miscUtils';

type Props = {
  salesOrders: SalesOrderInfo[];
};

const UpcomingList: React.FC<Props> = ({ salesOrders }) => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  const { translations } = useTranslation();
  const accountInfo = useAppSelector<IAccountInfo>(selectAccountInfo);
  const userRole: string = useAppSelector<string>(selectUserRole);
  const doctors: IDoctor[] = useAppSelector<IDoctor[]>(selectDoctors);
  const servicesTranslations = useAppSelector(selectServicesTranslations);
  const lang = (localStorage.getItem('lang') || 'en').toLowerCase().trim();
  

  const [salesOrderType, setSalesOrderType] = useState<
    | 'all'
    | 'diagnostic walkin'
    | 'consultation walkin'
    | 'diagnostic appointment'
    | 'consultation appointment'
  >('all');
      useEffect(() => {
        dispatch(fetchServicesTranslations());
      }, [dispatch]);

      const translatedServicesMap = useMemo(
  () => buildTranslatedServicesMap(servicesTranslations, lang),
  [servicesTranslations, lang]
);

  const displaySalesOrders = () => {
    if (salesOrderType === 'all') {
      const walkinAppointments = salesOrders.filter(
        (upcomings) => !upcomings.sales_order_appointment.start
      );
      const dateAppointments = salesOrders.filter(
        (upcomings) => upcomings.sales_order_appointment.start
      );
      return [...walkinAppointments, ...dateAppointments];
    }
    if (salesOrderType === 'diagnostic appointment') {
      return salesOrders?.filter(
        (salesOrder) =>
          (salesOrder.service_service_group.service_group_id === 1 ||
            salesOrder.service_service_group.service_group_id === 4) &&
          salesOrder.sales_order_appointment?.start
      );
    }
    if (salesOrderType === 'diagnostic walkin') {
      return salesOrders?.filter(
        (salesOrder) =>
          (salesOrder.service_service_group.service_group_id === 1 ||
            salesOrder.service_service_group.service_group_id === 4) &&
          !salesOrder.sales_order_appointment.start &&
          !salesOrder.work_order
      );
    }
    if (salesOrderType === 'consultation appointment') {
      return salesOrders?.filter(
        (salesOrder) =>
          (salesOrder.service_service_group.service_group_id === 3 ||
            salesOrder.service_service_group.service_group_id === 7 ||
            salesOrder.service_service_group.service_group_id === 8 ||
            salesOrder.service_service_group.service_group_id === 11) &&
          salesOrder.sales_order_appointment.start
      );
    }
    if (salesOrderType === 'consultation walkin') {
      return salesOrders?.filter(
        (salesOrder) =>
          (salesOrder.service_service_group.service_group_id === 3 ||
            salesOrder.service_service_group.service_group_id === 7 ||
            salesOrder.service_service_group.service_group_id === 8 ||
            salesOrder.service_service_group.service_group_id === 11) &&
          !salesOrder.sales_order_appointment.start
      );
    }
    return [];
  };

  const filteredSalesOrders = displaySalesOrders();

  const onChangeSalesOrderTypeHandler = (e: any) => {
    setSalesOrderType(e.target.value);
  };

  const getDoctorAssignedFullName = (salesOrder: SalesOrderInfo) => {
    const tempoDoctorAssigned = doctors.find((doctor) =>
      doctor.person_appointments.find((appointment) => {
        if (
          salesOrder?.sales_order_item.sales_order_id &&
          salesOrder?.sales_order_item.sales_order_item_id
        ) {
          return (
            appointment.sales_order_id == salesOrder.sales_order_item.sales_order_id &&
            appointment.sales_order_item_id == salesOrder.sales_order_item.sales_order_item_id
          );
        }
      })
    );
    if (tempoDoctorAssigned) {
      return `${tempoDoctorAssigned.person_firstname} ${tempoDoctorAssigned.person_lastname}`;
    }
    return undefined;
  };

  const handleNavigation = (isDiagnostic: boolean, salesOrder: any) => {
    if (isDiagnostic) {
      navigate(
        `/web/schedule?selectedTab=check-in&accountNumber=${salesOrder.customer_person.person_account_number}&salesOrderId=${salesOrder.sales_order.sales_order_id}&salesOrderItemId=${salesOrder?.sales_order_item?.sales_order_item_id}`
      );
      return;
    } else if (
      salesOrder?.service.service_id === ConsultationTypes.IN_CLINIK_CONSULTATION &&
      userRole === NURSE_USER_ROLE &&
      (salesOrder?.work_order?.work_order_status_id === 5 ||
        salesOrder?.work_order?.work_order_status_id === 6)
    ) {
      navigate('/web/prejoin-consultation', {
        state: {
          ...salesOrder,
          person: salesOrder?.customer_person,
          sales_order: salesOrder?.sales_order,
          sales_order_item: salesOrder?.sales_order_item,
          sales_order_appointment: salesOrder?.sales_order_appointment,
          service: salesOrder?.service,
          work_order: {
            ...salesOrder?.work_order,
            work_order_id: salesOrder?.work_order?.work_order_status_id,
          },
        },
      });
      return;
    }
    navigate(
      `/web/consultation/${salesOrder.sales_order.sales_order_id}/${salesOrder.sales_order_item?.sales_order_item_id}`
    );
    return;
  };

  return (
    <div className={classes.container}>
      <div className={classes.headerContainer}>
        <div className={classes.header}>{translations('upcomings')}</div>
        <select
          value={salesOrderType}
          onChange={onChangeSalesOrderTypeHandler}
          className={classes.salesOrderType}
        >
          <option value="all">{translations('all')}</option>
          {accountInfo.serves_diagnostics && (
            <>
              <option value="diagnostic appointment">{translations('test.appointment')}</option>
              <option value="diagnostic walkin">{translations('test.ohne.termin')}</option>
            </>
          )}
          {accountInfo.serves_consultations && (
            <>
              <option value="consultation appointment">
                {translations('consultation.appointment')}
              </option>
              <option value="consultation walkin">{translations('beratung.ohne.termin')}</option>
            </>
          )}
        </select>
      </div>
      <div className={classes.list}>
        {filteredSalesOrders.map((salesOrder) => {
          const doctorAssignedFullName = getDoctorAssignedFullName(salesOrder);
          const isDiagnostic =
            salesOrder?.service_service_group.service_group_id === 1 ||
            salesOrder?.service_service_group.service_group_id === 4;

          return (
            <div
              key={salesOrder.sales_order.sales_order_id}
              className={classes.listItem}
              onClick={() => handleNavigation(isDiagnostic, salesOrder)}
            >
              <div className={classes.customerInfo}>
                <div className={classes.customerName}>
                  {`${salesOrder.customer_person.person_firstname} ${salesOrder.customer_person.person_lastname}`}
                </div>
                {salesOrder.sales_order_appointment.start ? (
                  <div className={classes.appointmentTime}>
                    {`${moment(salesOrder.sales_order_appointment.start).format(
                      'DD.MM.YYYY / HH:mm'
                    )} - ${moment(salesOrder.sales_order_appointment.end_time).format('HH:mm')}`}
                  </div>
                ) : (
                  <div className={classes.appointmentTime}>
                    {moment(salesOrder.sales_order.datetime_create).format('DD.MM.YYYY')}
                  </div>
                )}
                {!isDiagnostic && (
                  <div className={classes.serviceName}>
                    {doctorAssignedFullName
                      ? `${translations('label.assigned')} ${doctorAssignedFullName}`
                      : translations('label_no.doctor.assigned')}
                  </div>
                )}
                <div className={classes.serviceName}>   {getTranslatedServicesName(
                  salesOrder.service.service_id,translatedServicesMap

                )}</div>
              </div>
              <div
                className={
                  isDiagnostic
                    ? classes.diagnosticCircle
                    : doctorAssignedFullName
                    ? classes.consultationCircle
                    : classes.notAssignedCircle
                }
              />
            </div>
          );
        })}
      </div>
    </div>
  );
};

export { UpcomingList };
